import { cn } from '@/lib/utils';
import { PlatformIcon } from '@/components/PlatformIcon';
import { AccountTypeBadge, FoundViaBadge } from '../AccountTypeBadge';
import { PresenterBadge } from '../PresenterBadge';
import { PageTag } from '../PageTag';
import { AudienceBadge } from '../AudienceBadge';
import { OutreachStatusBadge, doneReasonLabel } from '../OutreachStatus';
import { engagement, engagementTone, engagementTooltip, formatEngagement } from '../../lib/engagement';
import { profileUrl } from '../../lib/platform';
import type { LeadColumn } from './columnModel';
import { assigneeLabel, formatCount, formatDate, type LeadRow } from './rows';

/**
 * The lead table's column registry. One entry per column; the folder Leads
 * tab and the Creators screen each take the columns scoped to them. Order
 * here is display order.
 */
export const LEAD_COLUMNS: readonly LeadColumn[] = [
  {
    id: 'creator',
    header: 'Creator',
    locked: true,
    defaultVisible: true,
    scopes: ['folder', 'creators'],
    cell: (row) => <CreatorCell row={row} />,
  },
  {
    id: 'type',
    header: 'Type',
    defaultVisible: true,
    scopes: ['folder', 'creators'],
    cell: (row) => {
      const r = row.record;
      return (
        <span className="inline-flex items-center gap-1.5">
          <AccountTypeBadge accountType={r.accountType} confidence={r.accountTypeConfidence} signals={r.accountTypeSignals} />
          <PresenterBadge
            presenter={r.presenter}
            confidence={r.presenterConfidence}
            presentation={r.presenterPresentation}
            formats={r.presenterFormats}
            reason={r.presenterReason}
            profile={r.presenterProfile}
          />
          <PageTag enrichment={row.enrichment} />
        </span>
      );
    },
  },
  {
    id: 'followers',
    header: 'Followers',
    numeric: true,
    sortKey: 'followers',
    defaultVisible: true,
    scopes: ['folder', 'creators'],
    cell: (row) => formatCount(row.enrichment?.followers),
  },
  {
    id: 'avgViews',
    header: 'Avg views',
    numeric: true,
    sortKey: 'avgViews',
    defaultVisible: true,
    scopes: ['folder', 'creators'],
    cell: (row) => formatCount(row.enrichment?.avgViews),
  },
  {
    id: 'engagement',
    header: 'Engagement',
    numeric: true,
    sortKey: 'engagement',
    defaultVisible: true,
    scopes: ['folder', 'creators'],
    cell: (row) => <EngagementCell row={row} />,
  },
  {
    id: 'audience',
    header: 'Audience',
    defaultVisible: true,
    scopes: ['folder', 'creators'],
    cell: (row) => (
      <AudienceBadge region={row.enrichment?.region as string | undefined} audience={row.record.audienceGeo} />
    ),
  },
  {
    id: 'outreach',
    header: 'Outreach',
    defaultVisible: true,
    scopes: ['folder', 'creators'],
    cell: (row) => <OutreachStatusBadge creator={row.record} />,
  },
  {
    id: 'assignedTo',
    header: 'Assigned to',
    defaultVisible: true,
    scopes: ['folder', 'creators'],
    cell: (row) => {
      const name = assigneeLabel(row.assignedTo);
      const claimed = row.claimedByName;
      if (!name && !claimed) return <span className="text-muted-foreground">—</span>;
      return (
        <span
          className="block max-w-[11rem] truncate"
          title={[name ? `Assigned to ${name}` : 'Unassigned', claimed ? `Claimed by ${claimed}` : null].filter(Boolean).join(' · ')}
        >
          {name ?? <span className="text-muted-foreground">Unassigned</span>}
          {claimed && <span className="text-muted-foreground"> · {claimed}</span>}
        </span>
      );
    },
  },
  {
    id: 'foundVia',
    header: 'Found via',
    defaultVisible: true,
    scopes: ['folder'],
    cell: (row) =>
      row.discoveryMethod ? (
        <FoundViaBadge method={row.discoveryMethod} source={row.discoverySource} topicMatch={row.topicMatch} />
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    id: 'folders',
    header: 'Folders',
    defaultVisible: true,
    scopes: ['creators'],
    cell: (row) =>
      row.folderNames.length === 0 ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <span className="block max-w-[14rem] truncate text-muted-foreground" title={row.folderNames.join(', ')}>
          {row.folderNames.slice(0, 2).join(', ')}
          {row.folderNames.length > 2 && ` +${row.folderNames.length - 2}`}
        </span>
      ),
  },
  {
    id: 'added',
    header: 'Added',
    sortKey: 'added',
    defaultVisible: true,
    scopes: ['folder', 'creators'],
    cell: (row) => <span className="text-muted-foreground">{formatDate(row.addedAt)}</span>,
  },
  {
    id: 'doneReason',
    header: 'Done reason',
    defaultVisible: false,
    scopes: ['folder'],
    cell: (row) => <span className="text-muted-foreground">{doneReasonLabel(row.doneReason) ?? '—'}</span>,
  },
];

function CreatorCell({ row }: { row: LeadRow }): JSX.Element {
  const nickname = row.enrichment?.nickname;
  return (
    <span className="flex min-w-0 items-center gap-2">
      <PlatformIcon platform={row.platform} size={14} className="shrink-0 text-muted-foreground" />
      <span className="min-w-0">
        <a
          href={profileUrl(row.platform, row.username)}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium hover:underline"
          onClick={(ev) => ev.stopPropagation()}
        >
          @{row.username}
        </a>
        {nickname && <span className="block max-w-[14rem] truncate text-sm text-muted-foreground">{String(nickname)}</span>}
      </span>
    </span>
  );
}

function EngagementCell({ row }: { row: LeadRow }): JSX.Element {
  const eng = engagement(row.enrichment);
  const tone = engagementTone(eng);
  return (
    <span
      title={engagementTooltip(eng)}
      className={cn(
        tone === 'strong' ? 'text-success-strong' : tone === 'ok' ? 'text-foreground' : 'text-muted-foreground',
        !eng.precise && eng.value !== null && 'italic opacity-60',
      )}
    >
      {formatEngagement(eng)}
    </span>
  );
}
